import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    CircularProgress,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    LinearProgress,
    IconButton,
    Tooltip,
    Chip
} from '@mui/material';
import { green, orange, red } from '@mui/material/colors';
import { CheckCircle, Info, Warning } from '@mui/icons-material';
import { finalizeReview, getQuestionnaireResponseById, getRaidItemsByReviewEntity, getReviewQuestionAnswersWithDimension, getRaidItems, updateReviewComments, submitReview } from '../../services/api';
import { getUser } from '../../services/auth';
import { UserUtils } from '../../types';

// Define interfaces for our section summary data
interface SectionSummary {
    dimension: string;
    totalQuestions: number;
    answeredQuestions: number;
    percentageAnswered: number;
    totalRaids: number;
}

interface SubmitReviewDialogProps {
    open: boolean;
    onClose: () => void;
    responseId: number;
    onSubmitSuccess: () => void;
    onOverdueRaidDetected?: (raidId: number) => void;
    initialComments?: string;
    reviewStatus?: string;
}

const SubmitReviewDialog: React.FC<SubmitReviewDialogProps> = ({
    open,
    onClose,
    responseId,
    onSubmitSuccess,
    onOverdueRaidDetected,
    initialComments = '',
    reviewStatus = ''
}) => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [comments, setComments] = useState(initialComments);
    const [sectionSummaries, setSectionSummaries] = useState<SectionSummary[]>([]);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [totalAnswered, setTotalAnswered] = useState(0);
    const [totalRaids, setTotalRaids] = useState(0);
    const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
    const [nonIdealAnswersWithoutRaid, setNonIdealAnswersWithoutRaid] = useState<any[]>([]);
    const [showNonIdealDialog, setShowNonIdealDialog] = useState(false);
    const [overdueDialogOpen, setOverdueDialogOpen] = useState(false);
    const [overdueRaidIdToFix, setOverdueRaidIdToFix] = useState<number | null>(null);
    const [overdueDialogMessage, setOverdueDialogMessage] = useState('');
    const currentUser = getUser();
    const canPerformReview = UserUtils.canPerformReview(currentUser);
    const canSubmitReview = UserUtils.canSubmitReview(currentUser);
    const canCloseReview = canPerformReview && reviewStatus === 'SUBMITTED';
    const canSubmit = canSubmitReview && reviewStatus !== 'SUBMITTED';

    const getApiErrorDetail = (err: unknown, fallback: string): string => {
        if (err && typeof err === 'object' && 'response' in err) {
            const data = (err as { response?: { data?: unknown } }).response?.data;
            if (typeof data === 'string') return data;
            if (data && typeof data === 'object') {
                const d = data as { detail?: unknown; message?: unknown };
                if (typeof d.detail === 'string') return d.detail;
                if (typeof d.message === 'string') return d.message;
            }
        }
        if (err instanceof Error && err.message) return err.message;
        return fallback;
    };

    const getFirstRaidIdFromError = (message: string): number | null => {
        const match = message.match(/#(\d+)/);
        return match ? Number(match[1]) : null;
    };

    useEffect(() => {
        if (open && responseId) {
            loadSummaryData();
        }
    }, [open, responseId]);
    
    // Update comments whenever initialComments changes or dialog opens
    useEffect(() => {
        if (initialComments) {
            setComments(initialComments);
        }
    }, [initialComments, open]);

    const loadSummaryData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch all answers for this response using our new API that includes dimension info
            const answersResponse = await getReviewQuestionAnswersWithDimension(responseId);
            const answers = answersResponse.data || [];

            // Create a map to store answers by dimension
            const dimensionMap: { [key: string]: any[] } = {};
            
            // We'll count all the questions and answers directly from the data
            let totalQuestionsCount = 0;
            let totalAnsweredCount = 0;
            let totalRaidCount = 0;
            const nonIdealWithoutRaid: any[] = [];
            
            // Process each answer to build dimension summaries
            for (const answer of answers) {
                // Get dimension directly from the answer - our new API provides this
                const dimension = answer.review_dimension || 'Uncategorized';
                
                // For debugging - remove in production
                console.log('Answer with dimension and RAID count:', dimension, answer.raid_item_count, answer);
                
                // Ensure the dimension exists in our map
                if (!dimensionMap[dimension]) {
                    dimensionMap[dimension] = [];
                }
                
                // Add this question to our counts
                totalQuestionsCount++;
                
                // Check if the answer is provided
                const isAnswered = answer.answer_value && answer.answer_value.length > 0 && answer.answer_value[0] !== '';
                
                if (isAnswered) {
                    totalAnsweredCount++;
                }
                
                // Add isAnswered flag to the answer for easier reference
                answer.isAnswered = isAnswered;
                
                // Get RAID count from the API response (raid_item_count field)
                const raidCount = answer.raid_item_count || 0;
                answer.raidCount = raidCount;
                totalRaidCount += raidCount;
                
                // Check for non-ideal answers without RAID items
                if (answer.non_ideal_value_and_no_raid === true) {
                    nonIdealWithoutRaid.push(answer);
                }
                
                dimensionMap[dimension].push(answer);
            }
            
            // Create summary for each dimension
            const summaries: SectionSummary[] = [];
            
            for (const dimension in dimensionMap) {
                const questions = dimensionMap[dimension];
                const answeredQuestions = questions.filter(q => q.isAnswered);
                const raidCount = questions.reduce((total, q) => total + (q.raidCount || 0), 0);
                
                summaries.push({
                    dimension,
                    totalQuestions: questions.length,
                    answeredQuestions: answeredQuestions.length,
                    percentageAnswered: questions.length > 0 ? 
                        (answeredQuestions.length / questions.length) * 100 : 0,
                    totalRaids: raidCount
                });
            }
            
            // Sort dimensions alphabetically
            summaries.sort((a, b) => a.dimension.localeCompare(b.dimension));
            
            // Log the data for debugging
            console.log(`Total questions: ${totalQuestionsCount}, answered: ${totalAnsweredCount}, RAID items: ${totalRaidCount}`);
            
            // Update state with the collected data
            setSectionSummaries(summaries);
            setTotalQuestions(totalQuestionsCount);
            setTotalAnswered(totalAnsweredCount);
            setTotalRaids(totalRaidCount);
            setNonIdealAnswersWithoutRaid(nonIdealWithoutRaid);
            
            // Set the 'all questions answered' flag based on our counts
            const allAnswered = totalQuestionsCount > 0 && totalQuestionsCount === totalAnsweredCount;
            console.log(`All questions answered: ${allAnswered}`);
            console.log(`Non-ideal answers without RAID: ${nonIdealWithoutRaid.length}`);
            setAllQuestionsAnswered(allAnswered);
        } catch (err) {
            console.error("Error loading review summary data:", err);
            setError("Failed to load review summary data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            setError(null);

            if (!canCloseReview && !canSubmit) {
                setError("You do not have permission to submit or close this review.");
                return;
            }

            if (!canCloseReview) {
                if (!allQuestionsAnswered) {
                    setError("All questions must be answered before submitting the review.");
                    return;
                }

                if (nonIdealAnswersWithoutRaid.length > 0) {
                    setError("There are non-ideal answers without RAID items. Please address them before submitting.");
                    return;
                }
            }

            // UI safety-net: pre-check overdue OPEN RAID items before calling submit/close API.
            const overdueRaidId = await validateOverdueOpenRaidItemsOnUI();
            if (overdueRaidId) {
                const msg = 'Review cannot be submitted or closed because there are overdue OPEN RAID items. Please update due dates first.';
                setError(msg);
                setOverdueRaidIdToFix(overdueRaidId);
                setOverdueDialogMessage(msg);
                setOverdueDialogOpen(true);
                return;
            }
            
            // First, update comments if there are any
            if (comments.trim()) {
                await updateReviewComments(responseId, comments);
            }
            
            // Submit for reviewee flow; close for reviewer flow after submission.
            if (canCloseReview) {
                await finalizeReview(responseId);
            } else {
                await submitReview(responseId);
            }
            
            // Success
            onSubmitSuccess();
            onClose();
        } catch (err) {
            console.error("Error submitting review:", err);
            const detail = getApiErrorDetail(err, "Failed to submit review. Please try again.");
            setError(detail);

            const raidId = getFirstRaidIdFromError(detail);
            if (raidId) {
                setOverdueRaidIdToFix(raidId);
                setOverdueDialogMessage(detail);
                setOverdueDialogOpen(true);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleFixOverdueError = () => {
        if (overdueRaidIdToFix && onOverdueRaidDetected) {
            setOverdueDialogOpen(false);
            onOverdueRaidDetected(overdueRaidIdToFix);
        }
    };

    const validateOverdueOpenRaidItemsOnUI = async (): Promise<number | null> => {
        const response = await getQuestionnaireResponseById(responseId);
        const reviewEntityId = response?.data?.review_entity_id;
        if (!reviewEntityId) {
            return null;
        }

        const raidResponse = await getRaidItemsByReviewEntity(reviewEntityId);
        const raidItems = Array.isArray(raidResponse?.data) ? raidResponse.data : [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const getDueDateFieldsForCategory = (category: string): string[] => {
            const c = (category || '').toUpperCase();
            if (c === 'ACTION') return ['planned_competion_date', 'planned_completion_date'];
            if (c === 'DEPENDENCY') return ['expected_fulfuillment_date', 'expected_fulfillment_date'];
            if (c === 'ISSUE') return ['proposed_date'];
            if (c === 'ASSUMPTION') return ['proposed_date', 'contingency_proposed_date'];
            if (c === 'RISK') return ['proposed_date', 'contingency_proposed_date'];
            return [];
        };

        const parseIsoDate = (value: unknown): Date | null => {
            if (!value || typeof value !== 'string') return null;
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return null;
            d.setHours(0, 0, 0, 0);
            return d;
        };

        for (const item of raidItems) {
            const details = Array.isArray(item?.details) ? item.details : [];
            if (details.length === 0) continue;

            const latest = [...details].sort((a, b) => {
                const ta = new Date(a?.created_at || 0).getTime();
                const tb = new Date(b?.created_at || 0).getTime();
                return tb - ta;
            })[0];

            const latestStatus = String(latest?.status || '').trim().toUpperCase();
            if (latestStatus !== 'OPEN') continue;

            const latestDetails = latest?.details && typeof latest.details === 'object' ? latest.details : {};
            const dueFields = getDueDateFieldsForCategory(String(item?.raid_category || ''));

            for (const key of dueFields) {
                const due = parseIsoDate((latestDetails as Record<string, unknown>)[key]);
                if (due && due < today) {
                    console.warn('[Overdue RAID check] Blocking submit/close due to overdue OPEN RAID item', {
                        raid_id: item.raid_id,
                        raid_category: item.raid_category,
                        title: item.title,
                        due_field: key,
                        due_value: (latestDetails as Record<string, unknown>)[key],
                    });
                    return Number(item.raid_id);
                }
            }
        }

        return null;
    };

    const getColorForPercentage = (percentage: number): string => {
        if (percentage >= 100) return green[500];
        if (percentage >= 70) return orange[500];
        return red[500];
    };

    return (
        <Dialog 
            open={open} 
            onClose={submitting ? undefined : onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>{canCloseReview ? 'Close Review' : 'Submit Review'}</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>Review Summary</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>
                                    Total Questions: <strong>{totalQuestions}</strong>
                                </Typography>
                                <Typography>
                                    Answered Questions: <strong>{totalAnswered}</strong>
                                </Typography>
                                <Typography>
                                    Completion: <strong>{totalQuestions > 0 ? 
                                        `${Math.round((totalAnswered / totalQuestions) * 100)}%` : 
                                        'N/A'}</strong>
                                </Typography>
                                <Typography>
                                    Total RAIDs: <strong>{totalRaids}</strong>
                                </Typography>
                            </Box>

                            <LinearProgress 
                                variant="determinate"
                                value={totalQuestions > 0 ? (totalAnswered / totalQuestions) * 100 : 0}
                                sx={{ 
                                    height: 10, 
                                    borderRadius: 5,
                                    backgroundColor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getColorForPercentage(
                                            totalQuestions > 0 ? (totalAnswered / totalQuestions) * 100 : 0
                                        )
                                    }
                                }}
                            />
                        </Box>

                        {/* Non-ideal answers without RAID warning */}
                        {nonIdealAnswersWithoutRaid.length > 0 ? (
                            <Alert 
                                severity="warning" 
                                sx={{ mb: 3 }}
                                action={
                                    <Tooltip title="View questions with non-ideal answers that need RAID items">
                                        <IconButton
                                            color="inherit"
                                            size="small"
                                            onClick={() => setShowNonIdealDialog(true)}
                                        >
                                            <Info />
                                        </IconButton>
                                    </Tooltip>
                                }
                            >
                                <Typography variant="body2">
                                    <strong>{nonIdealAnswersWithoutRaid.length}</strong> question(s) have non-ideal answers without RAID items. 
                                    These must be addressed before submitting the review.
                                </Typography>
                            </Alert>
                        ) : allQuestionsAnswered && (
                            <Alert severity="success" sx={{ mb: 3 }}>
                                <Typography variant="body2">
                                    All questions are answered appropriately. Ready to submit!
                                </Typography>
                            </Alert>
                        )}

                        <TableContainer component={Paper} sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Dimension</strong></TableCell>
                                        <TableCell align="right"><strong>Total</strong></TableCell>
                                        <TableCell align="right"><strong>Answered</strong></TableCell>
                                        <TableCell align="right"><strong>Unanswered</strong></TableCell>
                                        <TableCell align="right"><strong>Completion</strong></TableCell>
                                        <TableCell align="right"><strong>RAIDs</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sectionSummaries.map((section) => {
                                        // Calculate unanswered questions
                                        const unansweredQuestions = section.totalQuestions - section.answeredQuestions;
                                        
                                        return (
                                            <TableRow key={section.dimension}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {section.dimension}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">{section.totalQuestions}</TableCell>
                                                <TableCell align="right" sx={{ color: green[700] }}>
                                                    {section.answeredQuestions}
                                                </TableCell>
                                                <TableCell align="right" sx={{ 
                                                    color: unansweredQuestions > 0 ? red[700] : green[700]
                                                }}>
                                                    {unansweredQuestions}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Typography 
                                                            variant="body2"
                                                            sx={{ color: getColorForPercentage(section.percentageAnswered) }}
                                                        >
                                                            {Math.round(section.percentageAnswered)}%
                                                        </Typography>
                                                        <Box sx={{ width: 40, height: 4 }}>
                                                            <LinearProgress 
                                                                variant="determinate" 
                                                                value={section.percentageAnswered}
                                                                sx={{
                                                                    height: '100%',
                                                                    borderRadius: 1,
                                                                    backgroundColor: '#e0e0e0',
                                                                    '& .MuiLinearProgress-bar': {
                                                                        backgroundColor: getColorForPercentage(section.percentageAnswered)
                                                                    }
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">{section.totalRaids}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!allQuestionsAnswered && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                All questions must be answered before submitting the review.
                            </Alert>
                        )}

                        <TextField
                            label="Additional Comments (Optional)"
                            multiline
                            rows={4}
                            fullWidth
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            disabled={submitting}
                        />

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={onClose} 
                    disabled={submitting}
                >
                    {canCloseReview ? 'Cancel' : (reviewStatus === 'SUBMITTED' ? 'Close' : 'Cancel')}
                </Button>
                {reviewStatus === 'SUBMITTED' && !canCloseReview ? (
                    <Button 
                        variant="contained" 
                        color="success"
                        disabled={true}
                        startIcon={<CheckCircle />}
                    >
                        Review Submitted
                    </Button>
                ) : (canCloseReview || canSubmit) ? (
                    <Button 
                        onClick={handleSubmit}
                        variant="contained"
                        color={canCloseReview ? "success" : "primary"}
                        disabled={canCloseReview ? (loading || submitting) : (loading || submitting || !allQuestionsAnswered || nonIdealAnswersWithoutRaid.length > 0)}
                    >
                        {submitting ? (
                            <CircularProgress size={24} />
                        ) : (
                            canCloseReview ? "Close Review" : "Submit Review"
                        )}
                    </Button>
                ) : null}
            </DialogActions>

            <Dialog
                open={overdueDialogOpen}
                onClose={() => setOverdueDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Cannot submit review</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {overdueDialogMessage || 'There are overdue OPEN RAID items. Please fix them before submitting or closing the review.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOverdueDialogOpen(false)}>Close</Button>
                    <Button
                        onClick={handleFixOverdueError}
                        variant="contained"
                        color="primary"
                        disabled={!overdueRaidIdToFix}
                    >
                        Fix the error
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog to show non-ideal answers without RAID items */}
            <Dialog
                open={showNonIdealDialog}
                onClose={() => setShowNonIdealDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning color="warning" />
                        <Typography variant="h6">
                            Questions Requiring RAID Items
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The following questions have non-ideal answers but no RAID items logged. 
                        Please create RAID items to address these issues before submitting the review.
                    </Typography>
                    
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Dimension</strong></TableCell>
                                    <TableCell><strong>Question</strong></TableCell>
                                    <TableCell><strong>Answer</strong></TableCell>
                                    <TableCell><strong>Ideal Answer</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {nonIdealAnswersWithoutRaid.map((answer) => (
                                    <TableRow key={answer.id}>
                                        <TableCell>
                                            <Chip 
                                                label={answer.review_dimension} 
                                                size="small" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {answer.review_question_text}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={answer.answer_value?.[0] || 'N/A'} 
                                                size="small" 
                                                color="error"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={answer.ideal_value || 'N/A'} 
                                                size="small" 
                                                color="success"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowNonIdealDialog(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default SubmitReviewDialog;
